<template>
  <div
    ref="wrapRef"
    class="buyer-gap-bars flex min-h-0 h-full min-w-0 w-full flex-col rounded-2xl border border-gray-modern-100 bg-white p-6 shadow-sm"
    role="region"
    :aria-label="ariaLabel"
  >
    <div class="flex items-start justify-between gap-3 shrink-0">
      <div class="min-w-0 flex-1">
        <h3 class="buyer-gap-bars__title">{{ title }}</h3>
        <p class="buyer-gap-bars__subtitle">{{ subtitle }}</p>
      </div>
      <ChartImageDownloadButton
        v-if="downloadable"
        :disabled="!canDownload"
        :busy="isDownloading"
        @click="downloadImage"
      />
    </div>

    <div class="buyer-gap-bars__chart mt-6 shrink-0">
      <svg
        class="buyer-gap-bars__svg block w-full"
        :viewBox="`0 0 ${svgW} ${svgH}`"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <g v-for="(b, i) in layout.bars" :key="i">
          <line
            v-if="b.count === 0"
            :x1="b.x"
            :y1="b.baselineY"
            :x2="b.x + b.width"
            :y2="b.baselineY"
            stroke="var(--gray-modern-200, #E3E8EF)"
            stroke-width="3"
            stroke-linecap="round"
          />
          <rect
            v-else
            :x="b.x"
            :y="b.barY"
            :width="b.width"
            :height="b.barHeight"
            :rx="2"
            :fill="b.fill"
          />
          <text
            :x="b.x + b.width / 2"
            :y="b.valueY"
            text-anchor="middle"
            class="buyer-gap-bars__value"
          >
            {{ formatCount(b.count) }}
          </text>
          <text
            :x="b.x + b.width / 2"
            :y="b.labelY"
            text-anchor="middle"
            class="buyer-gap-bars__axis-label"
          >
            {{ b.label }}
          </text>
        </g>
      </svg>
    </div>
    <div class="min-h-0 flex-1" aria-hidden="true" />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import ChartImageDownloadButton from "./ChartImageDownloadButton.vue";
import { useChartImageDownload } from "../utils/useChartImageDownload";

const BAR_COLOR_AT_LW = "var(--Brand-500, #347774)";
const BAR_COLOR_BELOW_LW = "var(--idh-orange, #DE703E)";

const barFillForIndex = (index) => (index === 0 ? BAR_COLOR_AT_LW : BAR_COLOR_BELOW_LW);

const props = defineProps({
  title: {
    type: String,
    default: "Number Workers by Living Wage Gap Size",
  },
  subtitle: {
    type: String,
    default:
      "Workers grouped by how far below the Living Wage Estimate their remuneration falls",
  },
  /** JSON string: { label: string, count: number }[] — from server (preferred over :bars) */
  barsJson: {
    type: String,
    default: "",
  },
  /** { label: string, count: number }[] — ordered left to right */
  bars: {
    type: Array,
    default: undefined,
  },
  downloadable: { type: Boolean, default: false },
  downloadContext: { type: String, default: "" },
});

const wrapRef = ref(null);
const measuredW = ref(280);

const margin = { top: 28, right: 4, bottom: 40, left: 4 };
const chartInnerH = 200;

const svgW = computed(() => Math.max(200, measuredW.value));
const svgH = computed(() => margin.top + chartInnerH + margin.bottom);

const barsFromServer = computed(() => {
  const s = props.barsJson;
  if (typeof s !== "string" || !s.trim()) return null;
  try {
    const data = JSON.parse(s);
    if (!Array.isArray(data) || data.length === 0) return null;
    return data;
  } catch {
    return null;
  }
});

const resolvedBars = computed(() => {
  if (barsFromServer.value) return barsFromServer.value;
  if (Array.isArray(props.bars) && props.bars.length > 0) return props.bars;
  return null;
});

const hasChartData = computed(
  () =>
    Array.isArray(barsFromServer.value) &&
    barsFromServer.value.some((bar) => Number(bar.count) > 0),
);

const { downloadImage, isDownloading, canDownload } = useChartImageDownload({
  getRootElement: () => wrapRef.value,
  title: () => props.title,
  downloadContext: () => props.downloadContext,
  hasData: hasChartData,
});

const layout = computed(() => {
  const w = svgW.value;
  const list =
    resolvedBars.value ??
    [
      { label: "0%", count: 0 },
      { label: "0.1-10%", count: 900 },
      { label: "10.1-20%", count: 2400 },
      { label: "20.1-30%", count: 2484 },
      { label: "30.1-40%", count: 3546 },
      { label: "40.1-50%", count: 2955 },
      { label: "Over 50%", count: 396 },
    ];
  const n = list.length;
  const innerW = w - margin.left - margin.right;
  const pad = 0.2;
  const step = innerW / n;
  const barW = step * (1 - pad);
  const offset = (step - barW) / 2;
  const maxC = Math.max(...list.map((d) => Number(d.count) || 0), 1);

  const bars = list.map((d, i) => {
    const count = Number(d.count) || 0;
    const x = margin.left + step * i + offset;
    const h = maxC > 0 ? (count / maxC) * chartInnerH : 0;
    const baselineY = margin.top + chartInnerH;
    const barY = baselineY - h;
    return {
      label: d.label,
      count,
      x,
      width: barW,
      barHeight: Math.max(h, 0),
      barY,
      baselineY,
      fill: barFillForIndex(i),
      valueY: count === 0 ? baselineY - 8 : barY - 8,
      labelY: baselineY + 22,
    };
  });

  return { bars, maxC };
});

const ariaLabel = computed(() => {
  const parts = layout.value.bars.map((b) => `${b.label}: ${b.count}`).join("; ");
  return `${props.title}. ${parts}`;
});

const formatCount = (n) =>
  typeof n === "number" && Number.isFinite(n) ? Math.round(n).toLocaleString("en-US") : "—";

let ro;

onMounted(() => {
  const el = wrapRef.value;
  const measure = () => {
    measuredW.value = el?.clientWidth ? el.clientWidth : 280;
  };
  measure();
  ro = new ResizeObserver(measure);
  if (el) ro.observe(el);
});

onBeforeUnmount(() => ro?.disconnect());
</script>

<style scoped>
.buyer-gap-bars__title {
  margin: 0;
  color: #111827;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.buyer-gap-bars__subtitle {
  margin: 8px 0 0;
  color: #9ca3af;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.buyer-gap-bars__chart {
  width: 100%;
  min-width: 0;
}

.buyer-gap-bars__value {
  fill: #111827;
  font-family: "Open Sans", sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: normal;
}

.buyer-gap-bars__axis-label {
  fill: #6b7280;
  font-family: "Open Sans", sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: normal;
}
</style>
